import { SeatSelectTypes } from "~components/reservation/constants";
import { log } from "~features/seated-reservation/helpers/stateLogger";
import { dontUnselectSeatsPrompt } from "~features/seated-reservation/helpers/userPrompt";
import {
  getAvailableCategoriesForChart,
  getSelectableZones,
} from "~features/seated-reservation/helpers/zoneFilters";
import { getState, setState, StoreState } from "../index";
import { selectBestSeats } from "./selectBestSeats";
import { ConvergeSdk } from "@seeka-labs/converge";

interface ActionProps {
  options: Partial<StoreState["options"]>;
}

export const updateOptions = async (
  payload: ActionProps,
  converge?: ConvergeSdk
) => {
  log("updateOptions", payload);

  try {
    const state = getState();

    if (dontUnselectSeatsPrompt(state.seats)) {
      return;
    }

    /**
    * ONLY updating the store with the default seat selection filter options fom the DB
    * The default seat selection values need to persist across navigation
    * Handling this separately avoids race conditions with the chart load
    */ 
    const isSeatSelectionFilterDefaults = Object.keys(payload.options).every(key => 
      ['amount', 'seatSelectType', 'selectedSort'].includes(key)
    );

    if (!state.chartReady && isSeatSelectionFilterDefaults) {
      setState({
        options: {
          ...state.options,
          ...payload.options,
        }
      });
      return;
    }

    const newOptions = payload.options;

    const changedCoverType =
      newOptions.coverType && state.options.coverType !== newOptions.coverType;

    const changedZoneType =
      newOptions.zoneType && state.options.zoneType !== newOptions.zoneType;

    const hasChanged = changedCoverType || changedZoneType;

    const changedState: Partial<StoreState> = {
      seats: hasChanged ? null : state.seats,
      // selectBestOnChange: hasChanged ? true : false,
      selectionType: hasChanged ? "none" : state.selectionType,
      options: {
        ...state.options,
        ...newOptions,
      },
    };

    if (hasChanged && state.chart) {
      const selectableZones = getSelectableZones(
        state.releasedZones,
        state.zones,
        changedState.options,
        state.isAdmin
      );

      const availableCategories = getAvailableCategoriesForChart(selectableZones);

      try {
        await Promise.all([
          state.chart.changeConfig({
            availableCategories: availableCategories?.length
              ? availableCategories
              : [""],
          }),
          state.chart.clearSelection(),
          state.chart.resetView(),
        ]);

        changedState.selectableZones = selectableZones;
        changedState.sidebarIndex = 1;
        changedState.currentSection = undefined;
        changedState.currentZone = undefined;
        changedState.seats = [];
        changedState.options.filterActive =
          newOptions.filterActive === false ? false : true;
      } catch (chartError) {
        console.error('Chanding chart config failed:', chartError);
      }
    }

    const isFreeSelect =
      (newOptions.seatSelectType || state.options.seatSelectType) ===
      SeatSelectTypes.FREE_SELECT;
    const amount = newOptions.amount || state.options.amount;

    if (state.chart) {
      try {
        const configPromise = state.chart.changeConfig({
          numberOfPlacesToSelect: isFreeSelect ? null : amount,
          maxSelectedObjects: isFreeSelect ? amount : null,
        });
        
        await Promise.race([
          configPromise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Chart config timeout')), 5000)
          )
        ]);
      } catch (chartError) {
        console.warn('Chart configuration warning:', chartError);
        // Updating the store even if chart config fails
        setState({
          options: {
            ...state.options,
            ...newOptions,
          }
        });
        return;
      }
    }

    setState({
      ...changedState,
      seats: [],
    });

    const hasAmountChanged =
      newOptions.amount && newOptions.amount !== state.options.amount;

    if (hasAmountChanged && state.seats?.length) {
      try {
        await selectBestSeats(undefined, converge);
      } catch (selectError) {
        console.error('Select best seats failed:', selectError);
      }
    }

  } catch (error) {
    console.error('UpdateOptions failed:', error);
    // Ensure store is updated even if other operations fail
    setState({
      options: {
        ...getState().options,
        ...payload.options,
      }
    });
  }
};